import { Editor, JSONContent } from '@tiptap/core';

import { Recipient } from '@bonusly/recipient-entities';

import { RichTextLexer, RichTextParser } from '../parser';
import { RichMediaState } from './rich-media-state';

const Tags = Object.freeze({
	AMOUNT: { name: 'amount', symbol: '+' },
	HASHTAG: { name: 'hashtag', symbol: '#' },
	MENTION: { name: 'mention', symbol: '&' },
	RECIPIENT: { name: 'recipient', symbol: '@' },
});
const symbol = {
	[Tags.AMOUNT.name]: Tags.AMOUNT.symbol,
	[Tags.HASHTAG.name]: Tags.HASHTAG.symbol,
	[Tags.MENTION.name]: Tags.MENTION.symbol,
	[Tags.RECIPIENT.name]: Tags.RECIPIENT.symbol,
};

const normalizeParagraphs = (jsonContent: JSONContent[]) => {
	if (!jsonContent.length) {
		return '';
	}

	const paragraphs = jsonContent.map((child) => {
		if (child && child.content) {
			return normalizeText(child.content || []);
		} else {
			return '';
		}
	});

	return paragraphs.join('\n');
};

const getMarkTypes = (node: JSONContent): { href?: string; bold?: boolean; italic?: boolean } => {
	const marks = node.marks as Array<{ type?: string; attrs?: { href?: string } }> | undefined;
	if (!marks) return {};
	return {
		href: marks.find((m) => m.type === 'link')?.attrs?.href,
		bold: marks.some((m) => m.type === 'bold'),
		italic: marks.some((m) => m.type === 'italic'),
	};
};

const normalizeText = (jsonContent: JSONContent[]) => {
	if (jsonContent.length === 0) {
		return '';
	}

	const normalizedText = jsonContent
		.map((child) => {
			if (child.type === 'text') {
				const text = child.text?.trim();
				if (!text) {
					return '';
				}

				const { href, bold, italic } = getMarkTypes(child);
				if (href) {
					return `[${text}](${href})`;
				}

				let result = text;
				if (bold) result = `**${result}**`;
				if (italic) result = `*${result}*`;
				return result;
			}

			const currentSymbol = symbol[child.type || ''];
			if (!currentSymbol || !child.attrs?.id) {
				return '';
			}

			return `${currentSymbol}${child.attrs.id}`.trim();
		})
		.filter(Boolean)
		.join(' ');

	return normalizedText.trim();
};

const parseRecipientRecord = (attrs: any) => {
	if (attrs.record) {
		return attrs.record;
	}
	return { username: attrs.id };
};

export const parseState = (state: Editor | string | null): RichMediaState => {
	switch (typeof state) {
		case 'string':
			return parseString(state);

		case 'object':
			return parseEditor(state);

		default:
			return new RichMediaState();
	}
};

export const parseString = (source: string): RichMediaState => {
	const tokens = new RichTextLexer(source).tokens();
	const json = new RichTextParser(tokens).document();

	let amount: number | undefined = undefined;

	const hashtags: string[] = [];
	const mentions: Recipient[] = [];
	const recipients: Recipient[] = [];
	const textNodes: string[] = [];

	tokens.forEach((token) => {
		if (token.type === 'amount' && amount === undefined) {
			amount = Number(token.value);
		}

		if (token.type === 'hashtag') {
			hashtags.push(token.value);
		}

		if (token.type === 'mention') {
			mentions.push(parseRecipientRecord({ record: { username: token.value } }));
		}

		if (token.type === 'recipient') {
			recipients.push(parseRecipientRecord({ record: { username: token.value } }));
		}

		if (token.type === 'text') {
			textNodes.push(token.value);
		}
	});

	const text = normalizeParagraphs(json?.content || []) || source;
	const plainText = textNodes.join(' ');

	return new RichMediaState({
		amount,
		hashtags,
		json,
		mentions,
		recipients,
		plainText,
		text,
	});
};

export const parseEditor = (editor: Editor | null): RichMediaState => {
	if (!editor) {
		return new RichMediaState();
	}

	let amount: number | undefined = undefined;

	const hashtags: string[] = [];
	const mentions: Recipient[] = [];
	const recipients: Recipient[] = [];
	const textNodes: string[] = [];

	editor.view.state.doc.descendants((node) => {
		if (node.type.name === 'amount') {
			amount = Number(node.attrs.id);
		}

		if (node.type.name === 'hashtag') {
			hashtags.push(node.attrs.id);
		}

		if (node.type.name === 'mention') {
			mentions.push(parseRecipientRecord(node.attrs));
		}

		if (node.type.name === 'recipient') {
			recipients.push(parseRecipientRecord(node.attrs));
		}

		if (node.type.name === 'text') {
			if (node.text) textNodes.push(node.text);
		}
	});

	const json = editor.getJSON() || {};
	const text = normalizeParagraphs(json?.content || []) || editor.getText();
	const plainText = textNodes.join('');

	return new RichMediaState({
		amount,
		hashtags,
		json,
		mentions,
		plainText,
		recipients,
		text,
	});
};
